import {
  ATTENDANCE_LEAVE_TYPE,
  type AttendanceLeaveType,
} from '@src/models/interfaces/attendanceLeaveType';
import {
  JOURNEY_ATTENDANCE_STATUS,
  REALTIME_AGGREGATE_STATUS,
  USER_ATTENDANCE_STATUS,
  USER_ROW_ATTENDANCE_STATE,
  type RealtimeAggregateStatusCode,
} from '@src/packages/workJourney/constants';

import type {
  AttendanceStatusDisplay,
  JourneyAttendanceStatusInput,
  UserAttendanceStatusInput,
  UserAttendanceStatusCode,
  JourneyAttendanceStatusCode,
  UserRowAttendanceStateCode,
} from './types';

/**
 * Minutes after the scheduled start, without a mark, at which an unmarked user
 * is considered ABSENT. Absolute from `scheduledStart` (not toleranceEnd + N).
 *
 * LATE is only reachable when `toleranceTimeMinutes < ABSENCE_THRESHOLD_MINUTES`.
 * If tolerance >= this value, unmarked users jump NOT_STARTED → ABSENT with no LATE.
 *
 * TODO: make this configurable per journey once the backend exposes the field;
 * fixed at 30 for now (product decision) and easy to change here.
 */
export const ABSENCE_THRESHOLD_MINUTES = 30;

const USER_STATUS_LABELS: Record<UserAttendanceStatusCode, AttendanceStatusDisplay> = {
  [USER_ATTENDANCE_STATUS.NOT_STARTED]: {
    code: USER_ATTENDANCE_STATUS.NOT_STARTED,
    color: 'default',
    labelId: 'workJourney.realTime.status.notStarted',
  },
  [USER_ATTENDANCE_STATUS.IN_TOLERANCE]: {
    code: USER_ATTENDANCE_STATUS.IN_TOLERANCE,
    color: 'orange',
    labelId: 'workJourney.realTime.status.inTolerance',
  },
  [USER_ATTENDANCE_STATUS.IN_PROGRESS]: {
    code: USER_ATTENDANCE_STATUS.IN_PROGRESS,
    color: 'green',
    labelId: 'workJourney.realTime.status.inProgress',
  },
  [USER_ATTENDANCE_STATUS.ABSENT]: {
    code: USER_ATTENDANCE_STATUS.ABSENT,
    color: 'red',
    labelId: 'workJourney.realTime.status.absent',
  },
};

const JOURNEY_STATUS_LABELS: Record<JourneyAttendanceStatusCode, AttendanceStatusDisplay> = {
  [JOURNEY_ATTENDANCE_STATUS.NOT_STARTED]: {
    code: JOURNEY_ATTENDANCE_STATUS.NOT_STARTED,
    color: 'default',
    labelId: 'workJourney.realTime.status.notStarted',
  },
  [JOURNEY_ATTENDANCE_STATUS.IN_TOLERANCE]: {
    code: JOURNEY_ATTENDANCE_STATUS.IN_TOLERANCE,
    color: 'orange',
    labelId: 'workJourney.realTime.status.inTolerance',
  },
  [JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS]: {
    code: JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS,
    color: 'green',
    labelId: 'workJourney.realTime.status.inProgress',
  },
  [JOURNEY_ATTENDANCE_STATUS.BELOW_MINIMUM]: {
    code: JOURNEY_ATTENDANCE_STATUS.BELOW_MINIMUM,
    color: 'red',
    labelId: 'workJourney.realTime.status.belowMinimum',
  },
  [JOURNEY_ATTENDANCE_STATUS.FINISHED]: {
    code: JOURNEY_ATTENDANCE_STATUS.FINISHED,
    color: 'default',
    labelId: 'workJourney.realTime.status.finished',
  },
};

const USER_ROW_STATE_LABELS: Record<UserRowAttendanceStateCode, AttendanceStatusDisplay> = {
  [USER_ROW_ATTENDANCE_STATE.NOT_STARTED]: {
    code: USER_ROW_ATTENDANCE_STATE.NOT_STARTED,
    color: 'default',
    labelId: 'workJourney.realTime.status.notStarted',
  },
  [USER_ROW_ATTENDANCE_STATE.ATTENDED]: {
    code: USER_ROW_ATTENDANCE_STATE.ATTENDED,
    color: 'green',
    labelId: 'workJourney.realTime.status.attended',
  },
  [USER_ROW_ATTENDANCE_STATE.LATE]: {
    code: USER_ROW_ATTENDANCE_STATE.LATE,
    color: 'gold',
    labelId: 'workJourney.realTime.status.late',
  },
  [USER_ROW_ATTENDANCE_STATE.ABSENT]: {
    code: USER_ROW_ATTENDANCE_STATE.ABSENT,
    color: 'red',
    labelId: 'workJourney.realTime.status.absent',
  },
};

/**
 * Leave that should force ABSENT immediately (before schedule windows).
 *
 * - Justified leave always counts as absent.
 * - Bare `unjustified_absence` with empty observations is the AR seed and
 *   follows NOT_STARTED → LATE → ABSENT windows (keeps "Sin marcar" correct).
 * - Unjustified with non-empty observations is an operator-reported absence
 *   (exception type / reason written onto the AR) and counts as absent now.
 */
const isAbsentLeaveType = (
  leaveType: AttendanceLeaveType | null,
  observations?: string | null,
): boolean => {
  if (leaveType === ATTENDANCE_LEAVE_TYPE.JUSTIFIED) {
    return true;
  }
  if (leaveType === ATTENDANCE_LEAVE_TYPE.UNJUSTIFIED) {
    return Boolean(observations?.trim());
  }
  return false;
};

const resolveJourneyStatusCode = (
  input: JourneyAttendanceStatusInput,
): JourneyAttendanceStatusCode => {
  const now = input.now ?? new Date();
  const nowSeconds = Math.floor(now.getTime() / 1000);
  const startSeconds = input.scheduledStartAtSeconds;
  const endSeconds = input.scheduledEndAtSeconds;

  if (startSeconds === null) {
    return JOURNEY_ATTENDANCE_STATUS.NOT_STARTED;
  }

  if (nowSeconds < startSeconds) {
    return JOURNEY_ATTENDANCE_STATUS.NOT_STARTED;
  }

  const toleranceEndSeconds = startSeconds + input.toleranceTimeMinutes * 60;
  // Tolerance window first (no marks yet) so IN_TOLERANCE is reachable when requiredMembers > 0.
  if (input.markedCount === 0 && nowSeconds < toleranceEndSeconds) {
    return JOURNEY_ATTENDANCE_STATUS.IN_TOLERANCE;
  }

  if (
    input.assignedCount < input.requiredMembers ||
    input.markedCount < input.requiredMembers
  ) {
    return JOURNEY_ATTENDANCE_STATUS.BELOW_MINIMUM;
  }

  if (endSeconds !== null && nowSeconds <= endSeconds) {
    return JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS;
  }

  if (endSeconds === null && nowSeconds >= startSeconds) {
    return JOURNEY_ATTENDANCE_STATUS.IN_PROGRESS;
  }

  // Past scheduledEndAt with minimum met — never fall back to NOT_STARTED.
  return JOURNEY_ATTENDANCE_STATUS.FINISHED;
};

const resolveUserRowStateCode = (
  input: UserAttendanceStatusInput,
): UserRowAttendanceStateCode => {
  const now = input.now ?? new Date();
  const nowSeconds = Math.floor(now.getTime() / 1000);

  if (isAbsentLeaveType(input.leaveType, input.observations)) {
    return USER_ROW_ATTENDANCE_STATE.ABSENT;
  }

  const startSeconds = input.scheduledStartAtSeconds;
  const toleranceEndSeconds =
    startSeconds === null ? null : startSeconds + input.toleranceTimeMinutes * 60;

  // Marked within tolerance → attended; marked after tolerance → late (never absent).
  if (input.actualStartAtSeconds !== null) {
    if (
      startSeconds !== null &&
      toleranceEndSeconds !== null &&
      input.actualStartAtSeconds > toleranceEndSeconds
    ) {
      return USER_ROW_ATTENDANCE_STATE.LATE;
    }
    return USER_ROW_ATTENDANCE_STATE.ATTENDED;
  }

  if (startSeconds === null || toleranceEndSeconds === null) {
    return USER_ROW_ATTENDANCE_STATE.NOT_STARTED;
  }

  // Before start or still within tolerance and unmarked: can still mark on time.
  if (nowSeconds < toleranceEndSeconds) {
    return USER_ROW_ATTENDANCE_STATE.NOT_STARTED;
  }

  // LATE window: [toleranceEnd, start + ABSENCE_THRESHOLD). Empty when tolerance >= threshold.
  const absenceThresholdSeconds = startSeconds + ABSENCE_THRESHOLD_MINUTES * 60;
  if (nowSeconds < absenceThresholdSeconds) {
    return USER_ROW_ATTENDANCE_STATE.LATE;
  }

  return USER_ROW_ATTENDANCE_STATE.ABSENT;
};

/** State shown per user in the "Por usuario" tab (marked/late/absent/not-started). */
export const resolveUserRowAttendanceState = (
  input: UserAttendanceStatusInput,
): AttendanceStatusDisplay => {
  const code = resolveUserRowStateCode(input);
  return USER_ROW_STATE_LABELS[code];
};

/** Single entry point for journey-level attendance status (swap implementation here later). */
export const resolveJourneyAttendanceStatus = (
  input: JourneyAttendanceStatusInput,
): AttendanceStatusDisplay => {
  const code = resolveJourneyStatusCode(input);
  return JOURNEY_STATUS_LABELS[code];
};

export const isJourneyBelowMinimum = (input: JourneyAttendanceStatusInput): boolean =>
  resolveJourneyStatusCode(input) === JOURNEY_ATTENDANCE_STATUS.BELOW_MINIMUM;

/** Marked on time or late — still present for KPIs / present counts. */
export const isUserPresent = (input: UserAttendanceStatusInput): boolean =>
  input.actualStartAtSeconds !== null &&
  !isAbsentLeaveType(input.leaveType, input.observations);

export const isUserAbsent = (input: UserAttendanceStatusInput): boolean =>
  resolveUserRowStateCode(input) === USER_ROW_ATTENDANCE_STATE.ABSENT;

/** Unmarked and not yet absent (NOT_STARTED or unmarked LATE). */
export const isUserNotMarked = (input: UserAttendanceStatusInput): boolean => {
  if (
    input.actualStartAtSeconds !== null ||
    isAbsentLeaveType(input.leaveType, input.observations)
  ) {
    return false;
  }
  const code = resolveUserRowStateCode(input);
  return (
    code === USER_ROW_ATTENDANCE_STATE.NOT_STARTED ||
    code === USER_ROW_ATTENDANCE_STATE.LATE
  );
};

/**
 * Still needs a mark or replace action: no start mark and no reported leave.
 * Includes computed ABSENT (past the 30′ threshold) so general-fix / requires-action
 * drawers stay populated when nobody has marked.
 */
export const isUserPendingMark = (input: UserAttendanceStatusInput): boolean =>
  input.actualStartAtSeconds === null &&
  !isAbsentLeaveType(input.leaveType, input.observations);

/**
 * Arrival delay in whole minutes past the planned start.
 * Unmarked: elapsed until `now`. Marked: elapsed until the actual start mark.
 */
export const getUserArrivalDelayMinutes = (input: UserAttendanceStatusInput): number | null => {
  if (input.scheduledStartAtSeconds === null) {
    return null;
  }

  const referenceSeconds =
    input.actualStartAtSeconds ??
    Math.floor((input.now ?? new Date()).getTime() / 1000);
  const elapsedMinutes = Math.floor(
    (referenceSeconds - input.scheduledStartAtSeconds) / 60,
  );

  return elapsedMinutes > 0 ? elapsedMinutes : null;
};

/** Resolves the intl message id for a status code (user-row, legacy user, or journey). */
export const getAttendanceStatusLabelId = (
  code:
    | UserRowAttendanceStateCode
    | UserAttendanceStatusCode
    | JourneyAttendanceStatusCode
    | RealtimeAggregateStatusCode,
): string | undefined => {
  if (code === REALTIME_AGGREGATE_STATUS.UNMARKED) {
    return 'workJourney.realTime.status.unmarked';
  }
  if (code === REALTIME_AGGREGATE_STATUS.PRESENT) {
    return 'workJourney.realTime.status.present';
  }
  return (
    USER_ROW_STATE_LABELS[code as UserRowAttendanceStateCode]?.labelId ??
    USER_STATUS_LABELS[code as UserAttendanceStatusCode]?.labelId ??
    JOURNEY_STATUS_LABELS[code as JourneyAttendanceStatusCode]?.labelId
  );
};
